import React, { JSX, Suspense, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';
import { orgSelector } from 'src/redux/selectors';
import { colorDefault } from 'src/utils/constants';
import OrganizationGuard from 'src/guards/organization.guard';
import BookingLayout from 'src/layouts/bookingLayout';
import { routeType } from 'src/utils/proptypes';
import AdminLayout from './adminLayout';
import BasicLayout from './basicLayout';
import HotelLayout from './hotelLayout';
import UserLayout from './userLayout';
import MapLayout from './mapLayout';
import NoneLayout from './noneLayout';
import { get } from 'lodash';
import OrganizationLayout from 'layouts/organizationLayout';
import SuspenseProgressBridge from 'src/components/SuspenseProgressBridge';
import SkipLink from 'src/components/SkipLink';
import RouteFocus from 'src/router/RouteFocus';
import { useLocation } from 'react-router';
import { LayoutEnum } from 'src/layouts/constants';

const layoutComponent = {
   [LayoutEnum.ADMIN]: AdminLayout,
   [LayoutEnum.BASIC]: BasicLayout,
   [LayoutEnum.ORGANIZATION]: OrganizationLayout,
   [LayoutEnum.MAP]: MapLayout,
   [LayoutEnum.HOTEL]: HotelLayout,
   [LayoutEnum.BOOKING]: BookingLayout,
   [LayoutEnum.USER]: UserLayout,
};

// Dynamic theme colors with fallback hierarchy
const applyTheme = (orgColor?: any) => {
   document.documentElement.style.setProperty('--color-primary', colorDefault.colorPrimary);
   document.documentElement.style.setProperty('--color-light-primary', colorDefault.colorLightPrimary);
   document.documentElement.style.setProperty('--color-secondary', colorDefault.colorSecondary);
   document.documentElement.style.setProperty('--color-tertiary', colorDefault.colorTertiary);
   document.documentElement.style.setProperty('--color-call-to-action', colorDefault.colorCallToAction);

   // Organization-specific colors: Priority 1. Org Config → 2. Hardcoded Default
   document.documentElement.style.setProperty('--color-org-light-primary', orgColor?.lightPrimary || colorDefault.colorLightPrimary);
   document.documentElement.style.setProperty('--color-org-primary', orgColor?.primary || colorDefault.colorPrimary);
   document.documentElement.style.setProperty('--color-org-secondary', orgColor?.secondary || colorDefault.colorSecondary);
   document.documentElement.style.setProperty('--color-org-view', orgColor?.view || colorDefault.colorView);
   document.documentElement.style.setProperty('--color-org-light-view', orgColor?.lightView || colorDefault.colorLightView);
   document.documentElement.style.setProperty('--color-org-call-to-action', orgColor?.callToAction || colorDefault.colorCallToAction);
   document.documentElement.style.setProperty('--color-org-header-text', orgColor?.headerText || colorDefault.colorHeaderText);
   document.documentElement.style.setProperty('--color-org-hero-text', orgColor?.heroText || colorDefault.colorHeroText);
   document.documentElement.style.setProperty('--color-org-start-background', orgColor?.startBackground || colorDefault.colorStartBackground);
   document.documentElement.style.setProperty('--color-org-end-background', orgColor?.endBackground || colorDefault.colorEndBackground);
};

type LayoutProps = {
   route: routeType;
   /** True once the SPA has navigated at least once; gates the focus move on route change. */
   navigated?: boolean;
};

const Layout: React.FC<LayoutProps> = ({ route, navigated = false }) => {
   const LayoutComponent: JSX.ElementType = get(layoutComponent, route.layout || '') || NoneLayout;
   const { pathname } = useLocation();

   const { organization } = useSelector(orgSelector);


   // document.title is owned by src/router/index.tsx (NPT-1466): it sets
   // "{title} | {siteName}" for routes that do not render their own <SEO>
   // (see `titleSource` in routes.tsx). Do not reintroduce an imperative
   // title/meta effect here: NPT-1072 removed one because it overrode the
   // per-route +title.ts and <SEO> values after hydration.

   // Handle Theme based on Layout Context
   useEffect(() => {
      const isOrgContext = [LayoutEnum.ORGANIZATION, LayoutEnum.MAP, LayoutEnum.HOTEL, LayoutEnum.BOOKING].includes(route.layout as LayoutEnum);

      const activeOrgColor = isOrgContext ? organization?.configuration?.color : undefined;

      applyTheme(activeOrgColor);
   }, [route.layout, organization]);

   const pageVariants = {
      initial: { opacity: 0, y: 20, scale: 0.98 },
      in: { opacity: 1, y: 0, scale: 1 },
      out: { opacity: 0, y: -20, scale: 1.02 },
   };

   const pageTransition = {
      type: 'tween' as const,
      ease: 'anticipate' as const,
      duration: 0.4,
   };

   return (
      <OrganizationGuard>
         <SkipLink />
         <AnimatePresence mode="wait">
            <motion.div key={route.group || route.path} initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
               <LayoutComponent>
                  <main id="main-content" tabIndex={-1} className="w-full flex-1 flex flex-col outline-none min-h-0">
                     <RouteFocus key={pathname} enabled={navigated} />
                     <Suspense fallback={<SuspenseProgressBridge />}>
                        <route.component type={route.type} permissions={route.permissions} />
                     </Suspense>
                  </main>
               </LayoutComponent>
            </motion.div>
         </AnimatePresence>
      </OrganizationGuard>
   );
};

export { AdminLayout, BasicLayout, HotelLayout, UserLayout };
export default Layout;
